import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import ServiceChargeModal from './components/ServiceChargeModal';
import ProcessingModal from './components/ProcessingModal';
import EmailReceiptModal from './components/EmailReceiptModal';
import ReadAloudButton from './components/ReadAloudButton';

const API = process.env.REACT_APP_BACKEND_URL;

function ABGPage({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check where user came from for navigation
  const fromLocation = searchParams.get('from');
  const returnPath = fromLocation === 'activity' ? '/user_dashboard' : '/homepage';
  const returnLabel = fromLocation === 'activity' ? 'Return to Dashboard' : 'Return to Home';
  
  // Dashboard metrics state
  const [reports, setReports] = useState([]);
  const [transactions, setTransactions] = useState([]);
  
  const [formData, setFormData] = useState({
    book_title: '',
    subtitle: '',
    author_name: user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : '',
    publisher: '',
    author_gender: 'male',
    current_biography: '',
    city_state: '',
    author_website: '',
    author_email: user?.email || '',
    additional_titles: [''],
    specializations: [''],
    achievements: [''],
    personal_interests: [''],
    professional_background: '',
    notable_quotes: [''],
    social_media_handles: {
      twitter: '',
      instagram: '',
      linkedin: '',
      facebook: '',
      youtube: ''
    }
  });
  
  const [manuscriptFile, setManuscriptFile] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [processingProgress, setProcessingProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [progressSteps, setProgressSteps] = useState([
    { text: 'Analyzing author information', completed: false, current: false },
    { text: 'Generating biography', completed: false, current: false },
    { text: 'Formatting results', completed: false, current: false }
  ]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [showEmailReceiptModal, setShowEmailReceiptModal] = useState(false);
  const [lastTransactionId, setLastTransactionId] = useState(null);
  
  // Load specific report if report ID is in URL
  useEffect(() => {
    const reportId = searchParams.get('report');
    if (reportId) {
      loadReport(reportId);
    }
  }, [searchParams.get('report')]);
  
  // Fetch dashboard metrics
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const reportsResponse = await axios.get(`${API}/api/abg/user-results`, {
          withCredentials: true
        });
        setReports(reportsResponse.data || []);
        const transactionsResponse = await axios.get(`${API}/api/payments/transactions`, {
          withCredentials: true
        });
        setTransactions(transactionsResponse.data || []);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      }
    };
    if (user) {
      fetchDashboardData();
    }
  }, [user]);
  
  const loadReport = async (reportId) => {
    try {
      const response = await axios.get(`${API}/api/abg/results/${reportId}`, {
        withCredentials: true
      });
      const reportData = response.data;
      document.title = `${reportData.author_name} - ${reportData.book_title} | ABG Report`;
      setResult(reportData);
    } catch (err) {
      console.error('Failed to load report:', err);
      setError('Failed to load report. Please try again.');
      navigate('/user_dashboard');
    }
  };
  
  // Input change handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('social_media_handles.')) {
      const platform = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        social_media_handles: {
          ...prev.social_media_handles,
          [platform]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleTitleChange = (index, value) => {
    const newTitles = [...formData.additional_titles];
    newTitles[index] = value;
    setFormData(prev => ({ ...prev, additional_titles: newTitles }));
  };
  
  const handleAddTitle = () => {
    setFormData(prev => ({
      ...prev,
      additional_titles: [...prev.additional_titles, '']
    }));
  };
  
  const handleRemoveTitle = (index) => {
    const newTitles = formData.additional_titles.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, additional_titles: newTitles }));
  };
  
  const handleSpecializationChange = (index, value) => {
    const newSpecs = [...formData.specializations];
    newSpecs[index] = value;
    setFormData(prev => ({ ...prev, specializations: newSpecs }));
  };
  
  const handleAddSpecialization = () => {
    setFormData(prev => ({
      ...prev,
      specializations: [...prev.specializations, '']
    }));
  };
  
  const handleRemoveSpecialization = (index) => {
    const newSpecs = formData.specializations.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, specializations: newSpecs }));
  };
  
  const handleAchievementChange = (index, value) => {
    const newAchievements = [...formData.achievements];
    newAchievements[index] = value;
    setFormData(prev => ({ ...prev, achievements: newAchievements }));
  };
  
  const handleAddAchievement = () => {
    setFormData(prev => ({
      ...prev,
      achievements: [...prev.achievements, '']
    }));
  };
  
  const handleRemoveAchievement = (index) => {
    const newAchievements = formData.achievements.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, achievements: newAchievements }));
  };
  
  const handleInterestChange = (index, value) => {
    const newInterests = [...formData.personal_interests];
    newInterests[index] = value;
    setFormData(prev => ({ ...prev, personal_interests: newInterests }));
  };
  
  const handleAddInterest = () => {
    setFormData(prev => ({
      ...prev,
      personal_interests: [...prev.personal_interests, '']
    }));
  };
  
  const handleRemoveInterest = (index) => {
    const newInterests = formData.personal_interests.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, personal_interests: newInterests }));
  };
  
  const handleQuoteChange = (index, value) => {
    const newQuotes = [...formData.notable_quotes];
    newQuotes[index] = value;
    setFormData(prev => ({ ...prev, notable_quotes: newQuotes }));
  };
  
  const handleAddQuote = () => {
    setFormData(prev => ({
      ...prev,
      notable_quotes: [...prev.notable_quotes, '']
    }));
  };
  
  const handleRemoveQuote = (index) => {
    const newQuotes = formData.notable_quotes.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, notable_quotes: newQuotes }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.book_title.trim() || !formData.author_name.trim()) {
      setError('Please provide both book title and author name.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    const submissionData = {
      name: 'Author Biography Generator',
      bookTitle: formData.author_name || 'ABG Generation'
    };
    
    try {
      // Check if user needs to pay BEFORE processing (only for À La Carte users)
      if (user?.role === 'a_la_carte') {
        // Show payment authorization modal BEFORE any processing or charging
        setPaymentInfo({
          amount: 5.00,
          currency: 'USD',
          description: 'Author Biography Generator - AI-powered professional author biographies',
          submission_data: submissionData,
          name: 'Author Biography Generator',
          bookTitle: formData.author_name || 'ABG Generation'
        });
        setShowPaymentModal(true);
        setLoading(false);
        return; // Stop here - wait for user to confirm payment
      }
      
      // Process the generation (for subscribers, admins, beta testers who don't need payment confirmation)
      await processGeneration();
      
    } catch (err) {
      console.error('ABG submission error:', err);
      setError(err.response?.data?.detail || err.message || 'An error occurred while generating your biography.');
      setLoading(false);
    }
  };
  
  const processGeneration = async () => {
    try {
      // Validate manuscript file is uploaded
      if (!manuscriptFile) {
        throw new Error('Please upload a manuscript file');
      }
      
      // Step 1: Upload and analyze
      setCurrentStep('Analyzing author information and manuscript...');
      setProgressSteps([
        { text: 'Analyzing author information', completed: false, current: true },
        { text: 'Generating biography', completed: false, current: false },
        { text: 'Formatting results', completed: false, current: false }
      ]);
      setProcessingProgress(10);
      
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('manuscript_file', manuscriptFile);
      submitData.append('author_name', formData.author_name);
      submitData.append('book_title', formData.book_title);
      submitData.append('author_gender', formData.author_gender);
      submitData.append('current_biography', formData.current_biography || '');
      submitData.append('city_state', formData.city_state || '');
      submitData.append('author_website', formData.author_website || '');
      submitData.append('author_email', formData.author_email || '');
      
      // Handle additional titles array
      const additionalTitles = formData.additional_titles.filter(t => t.trim()).join(', ');
      submitData.append('additional_titles', additionalTitles);
      
      setProcessingProgress(25);
      
      // Step 2: Generate biography
      setCurrentStep('Generating professional author biography...');
      setProgressSteps([
        { text: 'Analyzing author information', completed: true, current: false },
        { text: 'Generating biography', completed: false, current: true },
        { text: 'Formatting results', completed: false, current: false }
      ]);
      setProcessingProgress(40);
      
      const response = await axios.post(`${API}/api/abg/generate`, submitData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('ABG Generation Response:', response.data);
      
      // Step 3: Formatting
      setCurrentStep('Finalizing biography package...');
      setProgressSteps([
        { text: 'Analyzing author information', completed: true, current: false },
        { text: 'Generating biography', completed: true, current: false },
        { text: 'Formatting results', completed: false, current: true }
      ]);
      setProcessingProgress(85);
      
      // Set result to display the biographies
      setResult(response.data);
      
      // Complete
      setProgressSteps([
        { text: 'Analyzing author information', completed: true, current: false },
        { text: 'Generating biography', completed: true, current: false },
        { text: 'Formatting results', completed: true, current: false }
      ]);
      setProcessingProgress(100);
      setCurrentStep('Complete!');
      console.log('Result state set with:', response.data);
      
      // Refresh dashboard stats after successful service completion
      window.dispatchEvent(new CustomEvent('refreshDashboardStats'));
      
      // Update URL without reloading the page
      const newUrl = new URL(window.location);
      newUrl.searchParams.set('report', response.data.id);
      window.history.pushState({}, '', newUrl);
      
      // Update document title
      document.title = `${response.data.author_name} - ${response.data.book_title} | ABG Report`;
      
      // Show "Send Transaction Receipt" modal if transaction occurred
      if (response.data.transaction_id) {
        setLastTransactionId(response.data.transaction_id);
        setShowEmailReceiptModal(true);
      }
      
      toast.success('Biography generation completed!');
      
    } catch (err) {
      console.error('Generation error:', err);
      setError(err.response?.data?.detail || err.message || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePaymentSuccess = async () => {
    setShowPaymentModal(false);
    setLoading(true);
    
    try {
      // IMPORTANT: User confirmed willingness to pay
      // BUT actual charge happens in backend AFTER successful generation
      // This ensures users are only charged for successfully completed services
      
      await processGeneration();
    } catch (error) {
      console.error('Post-payment generation error:', error);
      setError('Payment successful, but generation failed. Please contact support.');
      setLoading(false);
    }
  };
  
  const handleSendReceipt = async () => {
    if (!lastTransactionId) {
      toast.error('No transaction ID available');
      return;
    }
    
    try {
      await axios.post(
        `${API}/api/send-receipt-email`,
        {
          transaction_id: lastTransactionId,
          email: user?.email
        },
        { withCredentials: true }
      );
      
      toast.success('Receipt sent to your email!');
      setShowEmailReceiptModal(false);
    } catch (error) {
      console.error('Send receipt error:', error);
      toast.error('Failed to send receipt. Please try again.');
    }
  };
  
  const exportToPDF = () => {
    if (!result) {
      toast.error('No biography data to export');
      return;
    }
    
    try {
      const pdf = new jsPDF();
      
      // Set up fonts and spacing
      const pageWidth = 190; // PDF width minus margins
      const leftMargin = 10;
      let yPosition = 20;
      
      // Header
      pdf.setFontSize(20);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(30, 64, 175); // Blue color
      
      const title = `${result.author_name} - Author Biography`;
      pdf.text(title, leftMargin, yPosition);
      yPosition += 10;
      
      // Book title
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Book: ${result.book_title}`, leftMargin, yPosition);
      yPosition += 7;
      
      // Book subtitle (if exists)
      if (result.subtitle) {
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'italic');
        pdf.setTextColor(120, 120, 120);
        pdf.text(`${result.subtitle}`, leftMargin, yPosition);
        yPosition += 10;
      } else {
        yPosition += 8;
      }
      
      // Brief Biography
      if (result.biography_brief) {
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(30, 64, 175);
        pdf.text('Short Biography (200+ words)', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(0, 0, 0);
        
        const briefBioLines = pdf.splitTextToSize(result.biography_brief, pageWidth);
        briefBioLines.forEach(line => {
          if (yPosition > 275) {
            pdf.addPage();
            yPosition = 20;
          }
          pdf.text(line, leftMargin, yPosition);
          yPosition += 5;
        });
        yPosition += 10;
      }
      
      // Short Biography
      if (result.biography_short) {
        if (yPosition > 220) {
          pdf.addPage();
          yPosition = 20;
        }
        
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(30, 64, 175);
        pdf.text('Medium Biography (500+ words)', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(0, 0, 0);
        
        const shortBioLines = pdf.splitTextToSize(result.biography_short, pageWidth);
        shortBioLines.forEach(line => {
          if (yPosition > 275) {
            pdf.addPage();
            yPosition = 20;
          }
          pdf.text(line, leftMargin, yPosition);
          yPosition += 5;
        });
        yPosition += 10;
      }
      
      // Long Biography
      if (result.biography_long) {
        if (yPosition > 220) {
          pdf.addPage();
          yPosition = 20;
        }
        
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(30, 64, 175);
        pdf.text('Long Biography (1000+ words)', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(0, 0, 0);
        
        const longBioLines = pdf.splitTextToSize(result.biography_long, pageWidth);
        longBioLines.forEach(line => {
          if (yPosition > 275) {
            pdf.addPage();
            yPosition = 20;
          }
          pdf.text(line, leftMargin, yPosition);
          yPosition += 5;
        });
      }
      
      // Footer on all pages
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Page ${i} of ${pageCount}`, 105, 287, { align: 'center' });
        pdf.text('Generated by Digital Ink AI - www.DigitalInkAI.com', 105, 292, { align: 'center' });
      }
      
      const filename = `ABG_${result.author_name.replace(/[^a-z0-9]/gi, '_')}_${new Date().getTime()}.pdf`;
      pdf.save(filename);
      
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    }
  };
  
  const handleNewGeneration = () => {
    setResult(null);
    setError('');
    
    // Clear URL parameters
    const newUrl = new URL(window.location);
    newUrl.searchParams.delete('report');
    window.history.pushState({}, '', newUrl);
    
    // Reset document title
    document.title = 'Author Biography Generator | Digital Ink AI';
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  return (
    <>
      {/* Print CSS to remove browser headers/footers */}
      <style>
        {`
          @media print {
            @page {
              margin: 0.5in;
              size: auto;
            }
            /* Hide browser-added headers and footers */
            @page { margin-top: 0; margin-bottom: 0; }
            html { margin: 0; }
            body { margin: 0; }
          }
        `}
      </style>
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-slate-100">
        {/* Header with Logo and Navigation */}
        <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/Digital_Ink_AI_Logo.png" 
                alt="Digital Ink AI Logo" 
                className="h-12 w-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png";
                }}
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/homepage')}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                🏠 Home
              </button>
              <button
                onClick={() => navigate('/user_dashboard')}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                📊 Dashboard
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Service Title - Always shown */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            📝 Author Biography Generator
          </h1>
          {!result && (
            <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Transform your personal story into professional marketing gold. Provide your information, and our AI crafts three polished biographies: Short (200+ words) for social media, Medium (500+ words) for book jackets, and Long (1000+ words) for websites. Get compelling narratives that highlight expertise, build reader trust, and establish authority across all platforms instantly.
            </p>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-300 text-red-700 px-6 py-4 rounded-lg mb-6 print:hidden">
            {error}
          </div>
        )}

        {/* Debug: {result ? 'SHOWING RESULTS' : 'SHOWING FORM'} */}
        {!result ? (
          <>
            {/* Pricing Info - Only shown on form page */}
            {user && (
              <div className="mb-8 bg-gradient-to-r from-blue-50 to-teal-50 border border-blue-200 rounded-lg p-6">
                <div className="text-center">
                  {user.role === 'subscriber' || user.role === 'admin' || user.role === 'beta_tester' ? (
                    <div>
                      <h3 className="text-xl font-bold text-green-900 mb-2">✨ Unlimited Free Access</h3>
                      <p className="text-green-700">Included with your subscription - create as many biographies as you need!</p>
                    </div>
                  ) : (
                    <div>
                      <h3 className="text-xl font-bold text-blue-900 mb-2">💎 Standard Service - $5.00</h3>
                      <p className="text-blue-700">Professional author biography package charged to your payment method on file</p>
                    </div>
                  )}
                </div>
              </div>
            )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 print:hidden">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-lg border border-slate-200">
                <div className="p-6 border-b border-slate-200">
                  <div className="text-center mb-4">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
                      alt="Digital Ink AI" 
                      className="h-10 mx-auto mb-3"
                    />
                    <h2 className="text-2xl font-bold text-slate-900">📄 Author Information</h2>
                    <p className="text-sm text-slate-600 mt-1">Powered by Digital Ink AI</p>
                  </div>
                </div>

                <div className="p-6">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Manuscript File *</label>
                      <p className="text-xs text-gray-500 mb-2">Upload your manuscript (PDF, DOCX, or TXT)</p>
                      <input
                        type="file"
                        accept=".pdf,.docx,.txt,.doc"
                        onChange={(e) => setManuscriptFile(e.target.files[0])}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        required
                      />
                      {manuscriptFile && (
                        <p className="text-xs text-green-600 mt-2">✓ {manuscriptFile.name}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Book Title *</label>
                      <input
                        type="text"
                        name="book_title"
                        value={formData.book_title}
                        onChange={handleInputChange}
                        placeholder="e.g., The Digital Revolution"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Book Subtitle <span className="text-gray-500 font-normal">(Optional)</span></label>
                      <input
                        type="text"
                        name="subtitle"
                        value={formData.subtitle}
                        onChange={handleInputChange}
                        placeholder="e.g., How Technology Changed Our World"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Author Name *</label>
                      <input
                        type="text"
                        name="author_name"
                        value={formData.author_name}
                        onChange={handleInputChange}
                        placeholder="e.g., R. Dale Brunet"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Publisher <span className="text-gray-500 font-normal">(Optional)</span></label>
                      <input
                        type="text"
                        name="publisher"
                        value={formData.publisher}
                        onChange={handleInputChange}
                        placeholder="e.g., Penguin Random House"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Author Gender *</label>
                      <select
                        name="author_gender"
                        value={formData.author_gender}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        required
                      >
                        <option value="male">Male (he/his)</option>
                        <option value="female">Female (she/her)</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Used for proper pronoun usage</p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Current Biography <span className="text-gray-500 font-normal">(Optional)</span></label>
                      <p className="text-xs text-gray-500 mb-2">Paste your existing biography or key background info (if available)</p>
                      <textarea
                        name="current_biography"
                        value={formData.current_biography}
                        onChange={handleInputChange}
                        rows={6}
                        placeholder="Paste your current biography here... (Optional)"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">City, State <span className="text-gray-500 font-normal">(Optional)</span></label>
                      <input
                        type="text"
                        name="city_state"
                        value={formData.city_state}
                        onChange={handleInputChange}
                        placeholder="e.g., Austin, Texas"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Author Website <span className="text-gray-500 font-normal">(Optional)</span></label>
                      <input
                        type="url"
                        name="author_website"
                        value={formData.author_website}
                        onChange={handleInputChange}
                        placeholder="https://www.yourwebsite.com"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Additional Published Titles <span className="text-gray-500 font-normal">(Optional)</span></label>
                      {formData.additional_titles.map((title, index) => (
                        <div key={index} className="flex gap-2 mb-2">
                          <input
                            type="text"
                            value={title}
                            onChange={(e) => handleTitleChange(index, e.target.value)}
                            placeholder="Book title"
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                          />
                          {formData.additional_titles.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveTitle(index)}
                              className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={handleAddTitle}
                        className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
                      >
                        + Add Title
                      </button>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Professional Background <span className="text-gray-500 font-normal">(Optional)</span></label>
                      <textarea
                        name="professional_background"
                        value={formData.professional_background}
                        onChange={handleInputChange}
                        rows={4}
                        placeholder="e.g., Former marketing executive, college professor, software engineer..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Writing Specializations <span className="text-gray-500 font-normal">(Optional)</span></label>
                      {formData.specializations.map((spec, index) => (
                        <div key={index} className="flex gap-2 mb-2">
                          <input
                            type="text"
                            value={spec}
                            onChange={(e) => handleSpecializationChange(index, e.target.value)}
                            placeholder="e.g., Historical fiction, Business strategy"
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                          />
                          {formData.specializations.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveSpecialization(index)}
                              className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={handleAddSpecialization}
                        className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
                      >
                        + Add Specialization
                      </button>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Notable Achievements <span className="text-gray-500 font-normal">(Optional)</span></label>
                      {formData.achievements.map((achievement, index) => (
                        <div key={index} className="flex gap-2 mb-2">
                          <input
                            type="text"
                            value={achievement}
                            onChange={(e) => handleAchievementChange(index, e.target.value)}
                            placeholder="e.g., NYT Bestseller, Award winner"
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                          />
                          {formData.achievements.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveAchievement(index)}
                              className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={handleAddAchievement}
                        className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
                      >
                        + Add Achievement
                      </button>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Personal Interests <span className="text-gray-500 font-normal">(Optional)</span></label>
                      {formData.personal_interests.map((interest, index) => (
                        <div key={index} className="flex gap-2 mb-2">
                          <input
                            type="text"
                            value={interest}
                            onChange={(e) => handleInterestChange(index, e.target.value)}
                            placeholder="e.g., Photography, Travel, Cooking"
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                          />
                          {formData.personal_interests.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveInterest(index)}
                              className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={handleAddInterest}
                        className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
                      >
                        + Add Interest
                      </button>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Notable Quotes <span className="text-gray-500 font-normal">(Optional)</span></label>
                      {formData.notable_quotes.map((quote, index) => (
                        <div key={index} className="flex gap-2 mb-2">
                          <input
                            type="text"
                            value={quote}
                            onChange={(e) => handleQuoteChange(index, e.target.value)}
                            placeholder="A meaningful quote or saying"
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                          />
                          {formData.notable_quotes.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveQuote(index)}
                              className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={handleAddQuote}
                        className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
                      >
                        + Add Quote
                      </button>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Social Media Handles <span className="text-gray-500 font-normal">(Optional)</span></label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Twitter</label>
                          <input
                            type="text"
                            name="social_media_handles.twitter"
                            value={formData.social_media_handles.twitter}
                            onChange={handleInputChange}
                            placeholder="@username"
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Instagram</label>
                          <input
                            type="text"
                            name="social_media_handles.instagram"
                            value={formData.social_media_handles.instagram}
                            onChange={handleInputChange}
                            placeholder="@username"
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">LinkedIn</label>
                          <input
                            type="text"
                            name="social_media_handles.linkedin"
                            value={formData.social_media_handles.linkedin}
                            onChange={handleInputChange}
                            placeholder="/in/username"
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Facebook</label>
                          <input
                            type="text"
                            name="social_media_handles.facebook"
                            value={formData.social_media_handles.facebook}
                            onChange={handleInputChange}
                            placeholder="/username"
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs text-gray-600 mb-1">YouTube</label>
                          <input
                            type="text"
                            name="social_media_handles.youtube"
                            value={formData.social_media_handles.youtube}
                            onChange={handleInputChange}
                            placeholder="/channel/username"
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="pt-6 border-t border-gray-200">
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 disabled:bg-gray-400 text-white font-bold py-4 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Generating Biography...' : 'Generate Author Biography with Digital Ink AI'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Generation Features */}
              <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">🎯 Generation Features</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start">
                    <span className="text-emerald-500 mr-2">✓</span>
                    <span>Three biography formats (Short, Medium, Long)</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-emerald-500 mr-2">✓</span>
                    <span>Professional expertise highlighting</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-emerald-500 mr-2">✓</span>
                    <span>Personal interest integration</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-emerald-500 mr-2">✓</span>
                    <span>Platform-optimized content</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-emerald-500 mr-2">✓</span>
                    <span>Author credibility establishment</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-emerald-500 mr-2">✓</span>
                    <span>Social media handle inclusion</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </>
        ) : (
          /* Results Display */
          <div className="space-y-8">
            {/* Header Controls */}
            <div className="flex justify-between items-center bg-white rounded-lg p-6 shadow-lg border print:hidden">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Author Name:</span>
                  <p className="font-semibold text-slate-900">{result.author_name || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-slate-500">Book Title:</span>
                  <p className="font-semibold text-slate-900">{result.book_title || 'N/A'}</p>
                </div>
                {result.subtitle && (
                  <div>
                    <span className="text-slate-500">Subtitle:</span>
                    <p className="font-semibold text-slate-900">{result.subtitle}</p>
                  </div>
                )}
                <div>
                  <span className="text-slate-500">Service:</span>
                  <p className="font-semibold text-slate-900">Author Biography Generator</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button onClick={() => window.print()} variant="outline" className="flex items-center gap-2">
                  🖨️ Print
                </Button>
                <Button onClick={exportToPDF} variant="outline" className="flex items-center gap-2">
                  📄 Export PDF
                </Button>
                <Button onClick={handleNewGeneration} className="bg-emerald-600 hover:bg-emerald-700">
                  📝 New Biography
                </Button>
              </div>
            </div>

            {/* Biography Metadata Card */}
            <Card className="bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-200 shadow-lg">
              <CardHeader className="pb-4">
                <CardTitle className="text-2xl font-bold text-slate-900">📋 Biography Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Prominent Title and Author */}
                  <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">{result.book_title}</h2>
                    {result.subtitle && (
                      <p className="text-lg text-slate-700 italic mb-2">{result.subtitle}</p>
                    )}
                    <p className="text-xl text-slate-800 mb-3">by {result.author_name}</p>
                    
                    {/* Publisher if exists */}
                    {result.publisher && (
                      <div className="mt-4 pt-3 border-t border-slate-200">
                        <span className="text-sm text-slate-600 font-semibold">Publisher: </span>
                        <span className="text-base text-slate-800">{result.publisher}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Biography Information Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <div className="text-sm text-slate-600 mb-1">Genre/Category</div>
                      <div className="text-lg font-semibold text-emerald-700">{result.genre || 'N/A'}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <div className="text-sm text-slate-600 mb-1">Writing Experience</div>
                      <div className="text-lg font-semibold text-emerald-700">{result.writing_experience || 'N/A'}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <div className="text-sm text-slate-600 mb-1">Target Audience</div>
                      <div className="text-lg font-semibold text-emerald-700">{result.target_audience || 'N/A'}</div>
                    </div>
                  </div>
                  
                  {/* Professional Background if exists */}
                  {result.professional_background && (
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <div className="text-sm text-slate-600 mb-2 font-semibold">Professional Background</div>
                      <div className="text-slate-800">{result.professional_background}</div>
                    </div>
                  )}
                  
                  {/* Achievements if exists */}
                  {result.achievements && (
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <div className="text-sm text-slate-600 mb-2 font-semibold">Notable Achievements</div>
                      <div className="text-slate-800">{result.achievements}</div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Brief Biography */}
            {result.biography_brief && (
              <Card className="bg-white shadow-lg border border-slate-200">
                <CardHeader className="bg-gradient-to-r from-emerald-50 to-green-50 border-b">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-2xl text-emerald-900">📄 Brief Biography (150-200 words)</CardTitle>
                    <div className="print:hidden">
                      <ReadAloudButton text={result.biography_brief} serviceType="abg" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="prose max-w-none text-slate-700 leading-relaxed">
                    {result.biography_brief.split('\n\n').map((paragraph, index) => (
                      <p key={index} className="mb-4 last:mb-0">{paragraph}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Short Biography */}
            {result.biography_short && (
              <Card className="bg-white shadow-lg border border-slate-200">
                <CardHeader className="bg-gradient-to-r from-emerald-50 to-green-50 border-b">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-2xl text-emerald-900">📄 Short Biography (200-500 words)</CardTitle>
                    <div className="print:hidden">
                      <ReadAloudButton text={result.biography_short} serviceType="abg" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="prose max-w-none text-slate-700 leading-relaxed">
                    {result.biography_short.split('\n\n').map((paragraph, index) => (
                      <p key={index} className="mb-4 last:mb-0">{paragraph}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Long Biography */}
            {result.biography_long && (
              <Card className="bg-white shadow-lg border border-slate-200">
                <CardHeader className="bg-gradient-to-r from-emerald-50 to-green-50 border-b">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-2xl text-emerald-900">📄 Long Biography (500-1500 words)</CardTitle>
                    <div className="print:hidden">
                      <ReadAloudButton text={result.biography_long} serviceType="abg" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="prose max-w-none text-slate-700 leading-relaxed">
                    {result.biography_long.split('\n\n').map((paragraph, index) => (
                      <p key={index} className="mb-4 last:mb-0">{paragraph}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Modals */}
        {showPaymentModal && (
          <ServiceChargeModal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            onConfirm={handlePaymentSuccess}
            serviceInfo={paymentInfo}
            user={user}
          />
        )}

        {loading && (
          <ProcessingModal
            isOpen={loading}
            serviceName="Author Biography Generator (ABG)"
            bookTitle={formData.book_title}
            progress={processingProgress}
            currentStep={currentStep}
            progressSteps={progressSteps}
          />
        )}

        {showEmailReceiptModal && lastTransactionId && (
          <EmailReceiptModal
            isOpen={showEmailReceiptModal}
            onClose={() => setShowEmailReceiptModal(false)}
            onSendReceipt={handleSendReceipt}
            serviceName="Author Biography Generator"
            bookTitle={formData.book_title}
            transactionId={lastTransactionId}
            userEmail={user?.email}
          />
        )}
      </main>
    </div>
    </>
  );
}

export default ABGPage;